/**
 * Dynamic Shopify Wishlist Engine & Global Controller
 * Manages localStorage persistence, live badge count updates,
 * floating toast notifications, and event delegation for all wishlist buttons.
 */
(function() {
  'use strict';

  const STORAGE_KEY = 'shopify_wishlist_items';

  // 1. Global Wishlist Data Manager
  if (!window.Wishlist) {
    window.ShopifyWishlist = window.Wishlist = {
      getItems: function() {
        try {
          const data = localStorage.getItem(STORAGE_KEY);
          return data ? JSON.parse(data) : [];
        } catch (e) {
          console.error('Wishlist getItems error:', e);
          return [];
        }
      },

      saveItems: function(items) {
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
          window.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { items: items } }));
        } catch (e) {
          console.error('Wishlist saveItems error:', e);
        }
      },

      hasItem: function(id) {
        if (!id) return false;
        const items = this.getItems();
        return items.some(item => String(item.id) === String(id));
      },

      addItem: function(product) {
        if (!product || !product.id) return false;
        let items = this.getItems();
        if (!this.hasItem(product.id)) {
          items.push(product);
          this.saveItems(items);
          return true;
        }
        return false;
      },

      removeItem: function(id) {
        if (!id) return false;
        let items = this.getItems();
        const newItems = items.filter(item => String(item.id) !== String(id));
        if (items.length !== newItems.length) {
          this.saveItems(newItems);
          return true;
        }
        return false;
      },

      toggleItem: function(product) {
        if (!product || !product.id) return false;
        if (this.hasItem(product.id)) {
          this.removeItem(product.id);
          return false;
        } else {
          this.addItem(product);
          return true;
        }
      },

      updateAllButtons: function() {
        const buttons = document.querySelectorAll('[data-wishlist-btn]');
        buttons.forEach(btn => {
          const id = btn.getAttribute('data-product-id');
          const txt = btn.querySelector('.pdp-wishlist-text');
          if (id && this.hasItem(id)) {
            btn.classList.add('is-active');
            btn.setAttribute('aria-label', 'Remove from Wishlist');
            if (txt) txt.textContent = 'Wishlisted';
          } else {
            btn.classList.remove('is-active');
            btn.setAttribute('aria-label', 'Add to Wishlist');
            if (txt) txt.textContent = 'Add to Wishlist';
          }
        });
      }
    };
  }

  // 2. Header Badge Updater
  function updateHeaderWishlistCount() {
    const countBadge = document.getElementById('header-wishlist-count');
    if (!countBadge) return;
    try {
      const count = window.Wishlist ? window.Wishlist.getItems().length : 0;
      countBadge.textContent = count;
      if (count > 0) {
        countBadge.classList.add('has-items');
      } else {
        countBadge.classList.remove('has-items');
      }
    } catch (e) {}
  }

  // 3. Floating Toast Notification
  function showWishlistToast(message, isAdded) {
    let toastContainer = document.getElementById('wishlist-toast-container');
    if (!toastContainer) {
      toastContainer = document.createElement('div');
      toastContainer.id = 'wishlist-toast-container';
      toastContainer.className = 'wishlist-toast-container';
      toastContainer.innerHTML = `
        <div class="wishlist-toast">
          <svg class="toast-heart" width="18" height="18" viewBox="0 0 24 24" fill="${isAdded ? '#e63946' : 'none'}" stroke="${isAdded ? '#e63946' : 'currentColor'}" stroke-width="2">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
          </svg>
          <span class="toast-msg"></span>
        </div>
      `;
      document.body.appendChild(toastContainer);
    }

    const msgSpan = toastContainer.querySelector('.toast-msg');
    const heartSvg = toastContainer.querySelector('.toast-heart');
    if (msgSpan) msgSpan.textContent = message;
    if (heartSvg) {
      heartSvg.setAttribute('fill', isAdded ? '#e63946' : 'none');
      heartSvg.setAttribute('stroke', isAdded ? '#e63946' : '#ffffff');
    }

    toastContainer.classList.add('is-visible');

    if (toastContainer.timeoutId) clearTimeout(toastContainer.timeoutId);
    toastContainer.timeoutId = setTimeout(function() {
      toastContainer.classList.remove('is-visible');
    }, 2500);
  }
  window.showWishlistToast = showWishlistToast;

  // 4. Auto-position PDP Wishlist Button directly under Variant Picker
  function positionPDPWishlistButton() {
    const wishlistWrapper = document.querySelector('.pdp-wishlist-wrapper');
    if (!wishlistWrapper) return;

    const variantSelectors = [
      'variant-picker',
      '.variant-picker',
      'variant-selects',
      '[data-testid="variant-picker"]',
      '.product-form__input',
      '.product-form__controls-group'
    ];

    let placed = false;
    for (let i = 0; i < variantSelectors.length; i++) {
      const variantPicker = document.querySelector(variantSelectors[i]);
      if (variantPicker && variantPicker.parentNode) {
        variantPicker.parentNode.insertBefore(wishlistWrapper, variantPicker.nextSibling);
        placed = true;
        break;
      }
    }

    if (!placed) {
      const buyButtonsSelectors = [
        'buy-buttons',
        '.buy-buttons',
        '[data-testid="buy-buttons"]',
        'form[action*="/cart/add"]',
        '.product-form__buttons',
        '.product-form'
      ];
      for (let j = 0; j < buyButtonsSelectors.length; j++) {
        const buyButtons = document.querySelector(buyButtonsSelectors[j]);
        if (buyButtons && buyButtons.parentNode) {
          buyButtons.parentNode.insertBefore(wishlistWrapper, buyButtons);
          break;
        }
      }
    }
  }

  // 5. Global Event Delegator for Wishlist Buttons
  function initGlobalWishlistEngine() {
    positionPDPWishlistButton();
    updateHeaderWishlistCount();
    if (window.Wishlist) window.Wishlist.updateAllButtons();

    document.addEventListener('click', function(e) {
      let btn = null;
      if (e.composedPath) {
        const path = e.composedPath();
        for (let i = 0; i < path.length; i++) {
          if (path[i].getAttribute && path[i].hasAttribute('data-wishlist-btn')) {
            btn = path[i];
            break;
          }
        }
      }
      if (!btn && e.target && e.target.closest) {
        btn = e.target.closest('[data-wishlist-btn]');
      }

      if (!btn) return;

      e.preventDefault();
      e.stopPropagation();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();

      const product = {
        id: btn.getAttribute('data-product-id'),
        variantId: btn.getAttribute('data-product-variant-id') || btn.getAttribute('data-product-id'),
        handle: btn.getAttribute('data-product-handle'),
        title: btn.getAttribute('data-product-title'),
        url: btn.getAttribute('data-product-url'),
        image: btn.getAttribute('data-product-img'),
        price: btn.getAttribute('data-product-price'),
        comparePrice: btn.getAttribute('data-product-compare-price'),
        vendor: btn.getAttribute('data-product-vendor')
      };

      if (!product.id) return;

      const isAdded = window.Wishlist.toggleItem(product);

      btn.classList.remove('wishlist-pop');
      void btn.offsetWidth;
      btn.classList.add('wishlist-pop');

      const txt = btn.querySelector('.pdp-wishlist-text');

      if (isAdded) {
        btn.classList.add('is-active');
        btn.setAttribute('aria-label', 'Remove from Wishlist');
        if (txt) txt.textContent = 'Wishlisted';
        showWishlistToast(`"${product.title || 'Product'}" added to Wishlist`, true);
      } else {
        btn.classList.remove('is-active');
        btn.setAttribute('aria-label', 'Add to Wishlist');
        if (txt) txt.textContent = 'Add to Wishlist';
        showWishlistToast(`"${product.title || 'Product'}" removed from Wishlist`, false);
      }
    }, true);

    window.addEventListener('wishlist:updated', function() {
      updateHeaderWishlistCount();
      if (window.Wishlist) window.Wishlist.updateAllButtons();
    });

    window.addEventListener('storage', function(e) {
      if (e.key === STORAGE_KEY) {
        updateHeaderWishlistCount();
        if (window.Wishlist) window.Wishlist.updateAllButtons();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initGlobalWishlistEngine);
  } else {
    initGlobalWishlistEngine();
  }
})();
